import React, { useState, useEffect } from 'react';
import LoadingState from './components/primitives/LoadingState';
import ThinkingState from './components/primitives/ThinkingState';
import StreamingText from './components/primitives/StreamingText';
import ApprovalCard from './components/primitives/ApprovalCard';
import ToolChips from './components/primitives/ToolChips';
import TaskRows from './components/primitives/TaskRows';
import ChatComposer from './components/primitives/ChatComposer';
import PromptBar from './components/primitives/PromptBar';
import SidebarNav from './components/primitives/SidebarNav';
import RecommendationCard from './components/primitives/RecommendationCard';
import ContextCards from './components/primitives/ContextCards';
import { DiffTable, RecordsTable } from './components/primitives/DiffTable';
import CodeBlock from './components/primitives/CodeBlock';
import SelectionToolbar from './components/primitives/SelectionToolbar';
import PrimitivesGallery from './components/primitives/PrimitivesGallery';
import { parseAgentIntent, AgentResponse } from './lib/agent-engine';

type Message = {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: number;
  model?: string;
  loadingLabel?: string;
  loadingVariant?: 'Drive' | 'Dots' | 'Orbit';
  showLoading?: boolean;
  thinkingVariant?: 'Steps' | 'Reasoning' | 'Search' | 'Coding';
  thinkingSummary?: string;
  tools?: any[];
  toolDiffs?: any[];
  taskRows?: any[];
  approvalQuestions?: any[];
  showApproval?: boolean;
  recommendation?: { title: string; confidence: number; highlights: string[] };
  contextCards?: any[];
  diffTable?: any;
  recordsTable?: any;
  codeBlock?: { lang: string; code: string };
  followups?: string[];
  hasChatComposer?: boolean;
};

const INITIAL_MESSAGES: Message[] = [
  {
    id: 'msg-1',
    role: 'user',
    content: 'Analyze our weekend creamery demand trends and generate waffle cone reorder actions for cone_king.',
    timestamp: Date.now() - 60000,
  },
  {
    id: 'msg-2',
    role: 'assistant',
    content: 'Pulled 3 summers of mint chip sales for comparison. Mint chip is up 12% with significantly stronger weekend peaks. Pistachio holds the top slot all weekend — churn it first thing Saturday so the batch has time to firm up before the afternoon rush.',
    timestamp: Date.now() - 30000,
    model: 'Sprinkles 5',
    thinkingVariant: 'Steps',
    thinkingSummary: 'Synthesized 3 summers of mint chip sales, evaluated cold-chain constraints, and computed lead-time models.',
    tools: [
      {
        icon: 'think',
        label: 'Thinking',
        chip: 'Planning the churn schedule…',
        mono: false,
        detailMono: false,
        detail: [
          { text: 'Weekend demand carries pistachio (+18%), so it churns first.' },
          { text: 'Batch capacity leaves two evening freezer windows.' }
        ]
      },
      {
        icon: 'write',
        label: 'Write 204 lines',
        chip: 'ChurnSchedule.tsx',
        mono: true,
        detailMono: true,
        detail: [
          { text: '+ const windows = slots.filter((s) => s.temp <= -12)', tone: 'add' },
          { text: '+ return schedule(windows, { hero: "pistachio" })', tone: 'add' }
        ]
      },
      {
        icon: 'run',
        label: 'Rebuild and verify',
        chip: 'npm run freeze',
        mono: true,
        detailMono: true,
        detail: [
          { text: '✓ built in 1.2s' },
          { text: '✓ 34 operational checks passed' }
        ]
      }
    ],
    toolDiffs: [
      { file: 'flavors.css', add: 13, del: 0 },
      { file: 'ChurnSchedule.tsx', add: 74, del: 41 },
      { file: 'menu.ts', add: 8, del: 2 }
    ],
    recommendation: {
      title: 'Reorder waffle cones from cone_king with lead time 7_days',
      confidence: 94,
      highlights: ['cone_king', '7_days', '1,500 units', '$420.00 total']
    },
    diffTable: {
      title: 'Proposed Batch Production Adjustments (Next 7 Days)',
      headers: ['Flavor Item', 'Previous Batch', 'Proposed Batch', 'Delta Status'],
      rows: [
        { type: 'added', cells: ['Pistachio Royale', '120 L', '240 L', '+100% Increase'] },
        { type: 'modified', cells: ['Mint Chocolate Chip', '300 L', '340 L', '+13.3% Adjusted'] },
        { type: 'modified', cells: ['Classic Madagascar Vanilla', '400 L', '420 L', '+5.0% Steady'] },
        { type: 'removed', cells: ['Rocky Road Campfire', '90 L', '0 L', 'Discontinued'] },
      ]
    },
    contextCards: [
      {
        source: 'Cold-chain SOP Manual #402',
        score: '0.98 similarity',
        snippet: 'Cold-chain certification must be verified before a new dairy or cone vendor can be added to the automated reorder workflow.'
      },
      {
        source: 'Q4 Velocity Performance Table',
        score: '0.92 similarity',
        snippet: 'Pistachio +18%, Vanilla +6%, Rocky Road -11%; retire flavors falling below 40 scoops weekly average.'
      }
    ],
    showApproval: true,
    approvalQuestions: [
      {
        q: 'How many waffle cone cases should we order?',
        type: 'radio',
        options: ['1,500 units (Weekend buffer)', '3,000 units (Full month stock)', '500 units (Just emergency)']
      },
      {
        q: 'Which delivery priority?',
        type: 'radio',
        options: ['Standard 7 business days', 'Expedited 3 days (+$45)', 'Next-day courier']
      },
      {
        q: 'Authorize budget dispatch?',
        type: 'check',
        options: ['Debit warehouse budget #402', 'Send invoice copy to accounting', 'Notify floor manager']
      }
    ],
    followups: [
      'Compare mint chip to last summer',
      'Which flavors sell best in winter?',
      'Export production batch sheet to CSV'
    ]
  },
];

export default function App() {
  const [theme, setTheme] = useState<'dark' | 'light'>('dark');
  const [messages, setMessages] = useState<Message[]>(INITIAL_MESSAGES);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [activeTab, setActiveTab] = useState('activity');
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [promptDraft, setPromptDraft] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  // Global Keyboard Shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setSearchOpen((prev) => !prev);
      }
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'b') {
        e.preventDefault();
        setSidebarOpen((prev) => !prev);
      }
      if (e.key === 'Escape') {
        setSearchOpen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleSendMessage = async (prompt: string, options: any = {}) => {
    if (!prompt.trim()) return;

    const userMsg: Message = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: prompt,
      timestamp: Date.now(),
    };

    // Analyze query intent intelligently
    const intent: AgentResponse = parseAgentIntent(prompt, options.sources || []);

    const assistantMsgId = `ast-${Date.now()}`;
    const assistantMsg: Message = {
      id: assistantMsgId,
      role: 'assistant',
      content: '',
      timestamp: Date.now(),
      model: options.model || 'Sprinkles 5',
      loadingLabel: intent.loadingLabel,
      loadingVariant: intent.loadingVariant,
      showLoading: true,
      thinkingVariant: intent.thinkingVariant,
      thinkingSummary: intent.thinkingSummary,
      tools: intent.tools,
      toolDiffs: intent.toolDiffs,
      taskRows: intent.taskRows,
      showApproval: Boolean(intent.approvalQuestions && intent.approvalQuestions.length > 0),
      approvalQuestions: intent.approvalQuestions,
      followups: intent.followups,
      hasChatComposer: intent.hasChatComposer,
      recommendation: prompt.toLowerCase().includes('reorder') || prompt.toLowerCase().includes('cone') ? {
        title: 'Reorder waffle cones from cone_king with lead time 7_days',
        confidence: 94,
        highlights: ['cone_king', '7_days', '1,500 units', '$420.00']
      } : undefined,
      diffTable: prompt.toLowerCase().includes('churn') || prompt.toLowerCase().includes('demand') ? {
        title: 'Proposed Batch Production Adjustments (Next 7 Days)',
        headers: ['Flavor Item', 'Previous Batch', 'Proposed Batch', 'Delta Status'],
        rows: [
          { type: 'added', cells: ['Pistachio Royale', '120 L', '240 L', '+100% Increase'] },
          { type: 'modified', cells: ['Mint Chocolate Chip', '300 L', '340 L', '+13.3% Adjusted'] },
          { type: 'modified', cells: ['Classic Madagascar Vanilla', '400 L', '420 L', '+5.0% Steady'] },
          { type: 'removed', cells: ['Rocky Road Campfire', '90 L', '0 L', 'Discontinued'] },
        ]
      } : undefined,
      contextCards: prompt.toLowerCase().includes('cert') || prompt.toLowerCase().includes('cold') || prompt.toLowerCase().includes('demand') ? [
        {
          source: 'Cold-chain SOP Manual #402',
          score: '0.98 similarity',
          snippet: 'Cold-chain certification must be verified before a new dairy or cone vendor can be added to the automated reorder workflow.'
        },
        {
          source: 'Q4 Velocity Performance Table',
          score: '0.92 similarity',
          snippet: 'Pistachio +18%, Vanilla +6%, Rocky Road -11%; retire flavors falling below 40 scoops weekly average.'
        }
      ] : undefined,
    };

    setMessages((prev) => [...prev, userMsg, assistantMsg]);
    setIsGenerating(true);

    // Try live OpenRouter stream, or intelligently transition simulated phases
    try {
      const response = await fetch('https://openrouter.ai/api/v1/chat/completions', {
        method: 'POST',
        headers: {
          'Authorization': 'Bearer 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',
          'Content-Type': 'application/json',
          'HTTP-Referer': 'https://beautifului.dev',
          'X-Title': 'Beautiful AI'
        },
        body: JSON.stringify({
          model: 'liquid/lfm-2.5-2.6b:free',
          messages: [
            { role: 'system', content: 'You are Beautiful AI, an intelligent, transparent AI assistant built with Beautiful UI primitives: structured reasoning traces, tool calling chips, human-in-the-loop approvals, and data tables.' },
            ...messages.slice(-4).map(m => ({ role: m.role, content: m.content })),
            { role: 'user', content: prompt }
          ],
          stream: true,
          temperature: 0.7
        })
      });

      if (!response.ok) throw new Error('Live API rate-limited');

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();
      let accumulated = '';

      while (reader) {
        const { value, done } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value, { stream: true });
        const lines = chunk.split('\n');

        for (const line of lines) {
          const trimmed = line.trim();
          if (!trimmed || !trimmed.startsWith('data: ')) continue;
          if (trimmed === 'data: [DONE]') continue;

          try {
            const data = JSON.parse(trimmed.slice(6));
            const delta = data.choices?.[0]?.delta?.content;
            if (delta) {
              accumulated += delta;
              setMessages((prev) =>
                prev.map((m) =>
                  m.id === assistantMsgId
                    ? { ...m, content: accumulated, showLoading: false }
                    : m
                )
              );
            }
          } catch {}
        }
      }
    } catch {
      // Intelligent Simulation Fallback with high-fidelity streaming
      setTimeout(() => {
        setMessages((prev) =>
          prev.map((m) =>
            m.id === assistantMsgId
              ? {
                  ...m,
                  showLoading: false,
                  content: intent.content,
                }
              : m
          )
        );
      }, 900);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSelectionAction = (action: string, text: string) => {
    if (action === 'rewrite') {
      handleSendMessage(`Rewrite and polish this passage with clarity:\n> "${text}"`);
    } else if (action === 'explain') {
      handleSendMessage(`Explain this concept in simple terms:\n> "${text}"`);
    } else if (action === 'bugs') {
      handleSendMessage(`Review this passage for potential bugs and leaks:\n> "${text}"`);
    } else if (action === 'summarize') {
      handleSendMessage(`Summarize the following into bullet points:\n> "${text}"`);
    }
  };

  return (
    <div className="flex h-screen w-screen overflow-hidden bg-canvas text-ink font-sans">
      {/* Floating Text Selection Toolbar */}
      <SelectionToolbar onAction={handleSelectionAction} />

      {/* Sidebar Nav */}
      <aside
        className={`shrink-0 border-r border-line bg-surface p-3 transition-all duration-300 ${
          sidebarOpen ? 'w-64' : 'w-0 -translate-x-full overflow-hidden p-0 border-0'
        }`}
      >
        <div className="flex h-full flex-col justify-between">
          <SidebarNav
            activeTab={activeTab}
            onTabChange={(tab) => setActiveTab(tab)}
            onNewTask={() => {
              setActiveTab('activity');
              setPromptDraft('Analyze weekend sales peaks and suggest reorder actions');
            }}
            onSearch={(q) => {
              setSearchQuery(q);
              setSearchOpen(true);
            }}
          />

          <div className="mt-auto pt-3 border-t border-line flex items-center justify-between text-xs text-ink-3">
            <span className="font-mono text-[11px]">✦ Beautiful UI</span>
            <button
              onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
              className="px-2 py-1 rounded-control bg-field hover:bg-hover text-ink transition-colors"
            >
              {theme === 'dark' ? '☀️ Light' : '🌙 Dark'}
            </button>
          </div>
        </div>
      </aside>

      {/* Main Workspace */}
      <main className="flex flex-1 flex-col h-full overflow-hidden relative">
        {/* Top Header */}
        <header className="h-14 shrink-0 border-b border-line px-5 flex items-center justify-between bg-surface/60 backdrop-blur-md z-20">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="size-8 rounded-control flex items-center justify-center text-ink-3 hover:bg-hover hover:text-ink transition-colors"
              title="Toggle Sidebar (Cmd+B)"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                <line x1="9" y1="3" x2="9" y2="21" />
              </svg>
            </button>

            {/* Quick View Navigation Tabs */}
            <div className="flex items-center gap-1 bg-field p-1 rounded-control">
              <button
                onClick={() => setActiveTab('activity')}
                className={`px-2.5 py-1 rounded-[6px] text-xs font-medium transition-colors ${
                  activeTab === 'activity' ? 'bg-surface text-ink font-semibold shadow-hairline' : 'text-ink-3 hover:text-ink'
                }`}
              >
                💬 Chat Workspace
              </button>
              <button
                onClick={() => setActiveTab('gallery')}
                className={`px-2.5 py-1 rounded-[6px] text-xs font-medium transition-colors ${
                  activeTab === 'gallery' ? 'bg-surface text-ink font-semibold shadow-hairline' : 'text-ink-3 hover:text-ink'
                }`}
              >
                ✨ Primitives Gallery
              </button>
              <button
                onClick={() => setActiveTab('tasks')}
                className={`px-2.5 py-1 rounded-[6px] text-xs font-medium transition-colors ${
                  activeTab === 'tasks' ? 'bg-surface text-ink font-semibold shadow-hairline' : 'text-ink-3 hover:text-ink'
                }`}
              >
                ⚡ Tasks
              </button>
              <button
                onClick={() => setActiveTab('dashboard')}
                className={`px-2.5 py-1 rounded-[6px] text-xs font-medium transition-colors ${
                  activeTab === 'dashboard' ? 'bg-surface text-ink font-semibold shadow-hairline' : 'text-ink-3 hover:text-ink'
                }`}
              >
                📬 Inbox
              </button>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setSearchOpen(true)}
              className="flex items-center gap-2 px-3 py-1.5 rounded-control bg-field text-xs text-ink-2 hover:bg-hover transition-colors"
            >
              <span>Quick Search</span>
              <kbd className="px-1 py-0.5 rounded bg-surface text-[10px] text-ink-3 shadow-hairline">/</kbd>
            </button>
            <button
              onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
              className="size-8 rounded-control flex items-center justify-center text-ink-3 hover:bg-hover hover:text-ink transition-colors"
            >
              {theme === 'dark' ? '☀️' : '🌙'}
            </button>
          </div>
        </header>

        {/* Tab View Switcher */}
        {activeTab === 'gallery' ? (
          /* Primitives Live Gallery & Playground */
          <PrimitivesGallery
            onSelectPrompt={(p) => {
              setActiveTab('activity');
              handleSendMessage(p);
            }}
          />
        ) : activeTab === 'tasks' ? (
          /* Agent Tasks View */
          <div className="flex-1 overflow-y-auto p-6 max-w-4xl mx-auto w-full flex flex-col gap-6 pb-36">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-bold text-ink">Active Agent Tasks</h2>
                <p className="text-xs text-ink-3">Live background execution pipeline and status checks</p>
              </div>
              <button
                onClick={() => {
                  setActiveTab('activity');
                  setPromptDraft('Build a supplier reorder task list');
                }}
                className="px-3 py-1.5 rounded-control bg-accent text-white text-xs font-medium"
              >
                + Create Task in Chat
              </button>
            </div>
            <TaskRows variant="Capsules" />
            <div className="mt-4">
              <h3 className="text-xs font-bold uppercase tracking-wider text-ink-3 mb-2">Detailed Task Logs</h3>
              <TaskRows variant="List" />
            </div>
          </div>
        ) : activeTab === 'dashboard' ? (
          /* Inbox / Human-in-the-loop Approvals View */
          <div className="flex-1 overflow-y-auto p-6 max-w-3xl mx-auto w-full flex flex-col gap-6 pb-36">
            <div>
              <h2 className="text-lg font-bold text-ink">Action Approvals Inbox</h2>
              <p className="text-xs text-ink-3">Human-in-the-loop confirmation required before agent execution</p>
            </div>
            <ApprovalCard />
            <div className="p-4 rounded-card bg-surface shadow-card border border-line">
              <div className="text-xs font-bold uppercase tracking-wider text-ink-3 mb-2">Audit History</div>
              <div className="text-xs text-ink-2">✓ Approved PO #8821 to cone_king ($420.00) · 2 hours ago</div>
              <div className="text-xs text-ink-2 mt-1">✓ Approved file patch ChurnSchedule.tsx · Yesterday</div>
            </div>
          </div>
        ) : activeTab === 'spaces' ? (
          /* Suppliers Directory View */
          <div className="flex-1 overflow-y-auto p-6 max-w-4xl mx-auto w-full flex flex-col gap-6 pb-36">
            <div>
              <h2 className="text-lg font-bold text-ink">Suppliers & Cold-Chain Directory</h2>
              <p className="text-xs text-ink-3">Verified dairy, cone, and ingredient vendors</p>
            </div>
            <RecordsTable />
          </div>
        ) : activeTab === 'analytics' ? (
          /* Inventory & Churn Composer View */
          <div className="flex-1 overflow-y-auto p-6 max-w-4xl mx-auto w-full flex flex-col gap-6 pb-36">
            <div>
              <h2 className="text-lg font-bold text-ink">Inventory Velocity & Batch Sweeps</h2>
              <p className="text-xs text-ink-3">Interactive batch comparison panel</p>
            </div>
            <DiffTable />
            <div className="mt-4">
              <h3 className="text-xs font-bold uppercase tracking-wider text-ink-3 mb-2">Interactive Churn Inspector</h3>
              <ChatComposer />
            </div>
          </div>
        ) : (
          /* Main Activity Chat View */
          <div className="flex-1 overflow-y-auto px-4 py-6 pb-36 flex flex-col items-center">
            <div className="w-full max-w-3xl flex flex-col gap-8">
              {messages.length === 0 ? (
                <div className="flex flex-col items-center justify-center text-center py-12">
                  <div className="size-14 rounded-2xl bg-gradient-to-tr from-accent to-green flex items-center justify-center text-white text-2xl font-bold mb-4 shadow-lg">
                    ✦
                  </div>
                  <h1 className="text-2xl font-bold tracking-tight text-ink mb-2">
                    Beautiful UI AI Workspace
                  </h1>
                  <p className="text-sm text-ink-2 max-w-md mb-8 leading-relaxed">
                    Crafted primitives for AI-native interfaces: pixel loaders, expandable traces, tool chips, approval cards, and dynamic prompt bar.
                  </p>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 w-full max-w-lg text-left">
                    <div
                      onClick={() => handleSendMessage('Analyze our weekend creamery demand trends and generate waffle cone reorder actions for cone_king.')}
                      className="p-3.5 rounded-card bg-surface shadow-card hover:bg-hover cursor-pointer transition-colors"
                    >
                      <div className="text-sm font-medium text-ink mb-1">🍦 Demand Analysis & Reorder</div>
                      <div className="text-xs text-ink-3">Traces inventory velocity, updates batch tables, and requests reorder approval.</div>
                    </div>
                    <div
                      onClick={() => handleSendMessage('Profile this reactive event listener code for memory leaks and provide an optimized cleanup hook.')}
                      className="p-3.5 rounded-card bg-surface shadow-card hover:bg-hover cursor-pointer transition-colors"
                    >
                      <div className="text-sm font-medium text-ink mb-1">💻 Code & Memory Audit</div>
                      <div className="text-xs text-ink-3">Runs tool calls, writes line diffs, and executes sandbox verification.</div>
                    </div>
                    <div
                      onClick={() => handleSendMessage('Search the web for top global artisan gelato suppliers and benchmark lead times.')}
                      className="p-3.5 rounded-card bg-surface shadow-card hover:bg-hover cursor-pointer transition-colors"
                    >
                      <div className="text-sm font-medium text-ink mb-1">🔍 Web & Supplier Search</div>
                      <div className="text-xs text-ink-3">Scans external sources with citations and lead time benchmarks.</div>
                    </div>
                    <div
                      onClick={() => handleSendMessage('Give me a detailed reasoning trace on cold-chain transport temperature requirements.')}
                      className="p-3.5 rounded-card bg-surface shadow-card hover:bg-hover cursor-pointer transition-colors"
                    >
                      <div className="text-sm font-medium text-ink mb-1">🧠 Deep Reasoning Trace</div>
                      <div className="text-xs text-ink-3">Multi-step prose reasoning and constraint validation.</div>
                    </div>
                  </div>
                </div>
              ) : (
                messages.map((msg) => (
                  <div
                    key={msg.id}
                    className={`flex flex-col w-full ${
                      msg.role === 'user' ? 'items-end' : 'items-start'
                    }`}
                  >
                    {msg.role === 'user' ? (
                      <div className="max-w-[85%] rounded-2xl bg-field px-4 py-2.5 text-sm text-ink shadow-sm border border-line">
                        {msg.content}
                      </div>
                    ) : (
                      <div className="w-full flex flex-col gap-3">
                        {/* Model Avatar Bar */}
                        <div className="flex items-center gap-2 text-xs font-semibold text-ink-2">
                          <span className="size-5 rounded-md bg-accent-tint text-accent flex items-center justify-center text-[11px]">
                            ✦
                          </span>
                          <span>{msg.model || 'Sprinkles 5'}</span>
                        </div>

                        {/* Loading State Primitive (Active while generating) */}
                        {msg.showLoading && (
                          <div className="py-1">
                            <LoadingState label={msg.loadingLabel || "Churning response"} variant={msg.loadingVariant || "Drive"} />
                          </div>
                        )}

                        {/* Thinking State Primitive */}
                        {msg.thinkingVariant && (
                          <ThinkingState
                            variant={msg.thinkingVariant}
                            customSummary={msg.thinkingSummary}
                          />
                        )}

                        {/* Tool Chips Primitive */}
                        {msg.tools && msg.tools.length > 0 && (
                          <ToolChips
                            customRows={msg.tools}
                            customDiffs={msg.toolDiffs}
                          />
                        )}

                        {/* Task Rows Primitive */}
                        {msg.taskRows && msg.taskRows.length > 0 && (
                          <TaskRows variant="Capsules" />
                        )}

                        {/* Streaming Text Body */}
                        {msg.content && (
                          <StreamingText
                            content={msg.content}
                            isLive={false}
                            onFollowupSelect={(f) => {
                              setPromptDraft(f);
                              handleSendMessage(f);
                            }}
                          />
                        )}

                        {/* Recommendation Card */}
                        {msg.recommendation && (
                          <RecommendationCard
                            title={msg.recommendation.title}
                            confidence={msg.recommendation.confidence}
                            highlights={msg.recommendation.highlights}
                            onPrimary={() => handleSendMessage('Proceed with reorder draft')}
                            onSecondary={() => setActiveTab('spaces')}
                          />
                        )}

                        {/* Diff Table */}
                        {msg.diffTable && (
                          <DiffTable
                            title={msg.diffTable.title}
                            headers={msg.diffTable.headers}
                            rows={msg.diffTable.rows}
                          />
                        )}

                        {/* Context Cards */}
                        {msg.contextCards && (
                          <ContextCards cards={msg.contextCards} />
                        )}

                        {/* Code Block if present */}
                        {msg.codeBlock && (
                          <CodeBlock lang={msg.codeBlock.lang} code={msg.codeBlock.code} />
                        )}

                        {/* Approval Card Primitive */}
                        {msg.showApproval && (
                          <ApprovalCard
                            customQuestions={msg.approvalQuestions}
                            onFinish={() => {
                              handleSendMessage('Answers approved and confirmed. Proceed with purchase order dispatch.');
                            }}
                          />
                        )}
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* Bottom Fixed Prompt Bar */}
        <div className="absolute bottom-0 inset-x-0 p-4 bg-gradient-to-t from-canvas via-canvas/90 to-transparent flex justify-center pointer-events-none z-30">
          <div className="w-full max-w-3xl pointer-events-auto flex justify-center">
            <PromptBar
              variant="Rounded"
              initialDraft={promptDraft}
              onSend={(prompt, options) => {
                setPromptDraft('');
                handleSendMessage(prompt, options);
              }}
            />
          </div>
        </div>
      </main>

      {/* Global Cmd+K / Quick Search Modal */}
      {searchOpen && (
        <div
          onClick={() => setSearchOpen(false)}
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-lg rounded-card bg-surface shadow-raised border border-line overflow-hidden"
          >
            <div className="flex items-center gap-2 px-4 py-3 border-b border-line">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-ink-3">
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              <input
                autoFocus
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search conversations, sources, or commands..."
                className="min-w-0 flex-1 bg-transparent text-sm text-ink outline-none"
              />
              <button
                onClick={() => setSearchOpen(false)}
                className="text-xs text-ink-3 hover:text-ink px-1.5 py-0.5 rounded bg-field"
              >
                ESC
              </button>
            </div>
            <div className="p-2 flex flex-col gap-1 max-h-72 overflow-y-auto">
              <div
                onClick={() => {
                  setActiveTab('gallery');
                  setSearchOpen(false);
                }}
                className="p-2.5 rounded-control hover:bg-hover cursor-pointer text-sm"
              >
                <div className="font-medium text-ink">✨ Open Primitives Live Gallery</div>
                <div className="text-xs text-ink-3">Interactive playground for all 12 crafted primitives</div>
              </div>
              <div
                onClick={() => {
                  setActiveTab('activity');
                  handleSendMessage('Analyze our weekend creamery demand trends and generate waffle cone reorder actions for cone_king.', { model: 'Sprinkles 5' });
                  setSearchOpen(false);
                }}
                className="p-2.5 rounded-control hover:bg-hover cursor-pointer text-sm"
              >
                <div className="font-medium text-ink">🍦 Weekend Creamery Demand Analysis</div>
                <div className="text-xs text-ink-3">Search sales metrics and lead times</div>
              </div>
              <div
                onClick={() => {
                  setActiveTab('activity');
                  handleSendMessage('Profile this reactive event listener code for memory leaks and provide an optimized cleanup hook.', { model: 'Sprinkles 5' });
                  setSearchOpen(false);
                }}
                className="p-2.5 rounded-control hover:bg-hover cursor-pointer text-sm"
              >
                <div className="font-medium text-ink">💻 React Performance Audit</div>
                <div className="text-xs text-ink-3">Inspect detached DOM listeners</div>
              </div>
              <div
                onClick={() => {
                  setActiveTab('tasks');
                  setSearchOpen(false);
                }}
                className="p-2.5 rounded-control hover:bg-hover cursor-pointer text-sm"
              >
                <div className="font-medium text-ink">⚡ Jump to Agent Tasks Pipeline</div>
                <div className="text-xs text-ink-3">View live running and completed tasks</div>
              </div>
              <div
                onClick={() => {
                  setActiveTab('dashboard');
                  setSearchOpen(false);
                }}
                className="p-2.5 rounded-control hover:bg-hover cursor-pointer text-sm"
              >
                <div className="font-medium text-ink">📬 Jump to Approvals Inbox</div>
                <div className="text-xs text-ink-3">Review pending human-in-the-loop decisions</div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
